import React, { useCallback } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { withErrorBoundary } from 'react-error-boundary';
import { useFeature } from '@ole-ui/ole-ui-components';
import useMobxStores from 'hooks/useMobxStores';
import { EnablementFeatureFlags } from 'config/featureFlags';
import ComponentError from 'components/Error/ComponentError';
import { handleError } from 'services/ErrorService';
import LearningPathWidget from 'components/LearningPath/Widget';
import MyProgressWidget from 'components/MyProgress';
import ClassesWidget from 'components/Classes/Widget';
import ErrorIcon from '@patternfly/react-icons/dist/esm/icons/error-circle-o-icon';
import ArrowLeftIcon from '@patternfly/react-icons/dist/esm/icons/arrow-left-icon';
import Banners from 'views/MyActivity/Banners';
import RHLSTrialWidget from 'components/RHLSTrialWidget';
import {
  Button,
  EmptyState,
  EmptyStateBody,
  EmptyStateHeader,
  EmptyStateIcon,
} from '@patternfly/react-core';
import { LiveEvent } from 'types';
import LaneIds, { LaneConfig, laneAnchorId } from './Lanes/types';
import ContinueLearningLane from './Lanes/ContinueLearningLane';
import TrialSubscriptionsLane from './Lanes/TrialSubscriptionsLane';
import SkillsAssessmentsLane from './Lanes/SkillsAssessmentsLane';
import SkillsPathsLane from './Lanes/SkillsPathsLane';
import LiveEventsLane from './Lanes/LiveEventsLane';
import SavedForLaterLane from './Lanes/SavedForLaterLane';
import NewOfferingsLane from './Lanes/NewOfferingsLane';

import './styles.scss';

type MyTrainingProps = {
  expandedLane: LaneIds | null;
  liveEvents?: LiveEvent[];
  setExpandedLane: React.Dispatch<React.SetStateAction<LaneIds | null>>;
};

const MyTraining = ({
  expandedLane,
  liveEvents = [],
  setExpandedLane,
}: MyTrainingProps) => {
  const { t } = useTranslation();
  const { catalogStore, classesStore, featuresStore, userStore } =
    useMobxStores();

  const isSkillsPathsEnabled = useFeature({
    defaultFlagValue: false,
    flagName: EnablementFeatureFlags.SkillsPathsEnabled,
    section: 'enablements',
  });

  const isNewMyTrainingEnabled = useFeature({
    defaultFlagValue: false,
    flagName: EnablementFeatureFlags.NewMyTraining,
    section: 'enablements',
  });

  const savedForLaterCardsEnabled = useFeature({
    defaultFlagValue: false,
    flagName: EnablementFeatureFlags.SavedForLaterCardsEnabled,
    section: 'enablements',
  });

  const newOfferingsEnabled = useFeature({
    defaultFlagValue: false,
    flagName: EnablementFeatureFlags.NewOfferingsLaneEnabled,
    section: 'enablements',
  });

  const { hasCurrentEnrollment } = classesStore;

  const showLearningPaths =
    featuresStore.allFeaturesFetched && isSkillsPathsEnabled;

  const handleTitleClick = useCallback(
    (lane: LaneIds | null) =>
      setExpandedLane((current) => (current === lane ? null : lane)),
    [setExpandedLane],
  );

  const lanes: LaneConfig[] = [
    {
      component: (
        <LiveEventsLane
          expanded={expandedLane === LaneIds.LiveEvents}
          liveEvents={liveEvents}
          onTitleClick={() => handleTitleClick(LaneIds.LiveEvents)}
        />
      ),
      id: LaneIds.LiveEvents,
      visible: liveEvents.length > 0,
    },
    {
      component: (
        <ContinueLearningLane
          expanded={expandedLane === LaneIds.ContinueLearning}
          onTitleClick={() => handleTitleClick(LaneIds.ContinueLearning)}
        />
      ),
      id: LaneIds.ContinueLearning,
      visible: true,
    },
    {
      component: (
        <SkillsAssessmentsLane
          expanded={expandedLane === LaneIds.SkillsAssessments}
          onTitleClick={() => handleTitleClick(LaneIds.SkillsAssessments)}
        />
      ),
      id: LaneIds.SkillsAssessments,
      visible: catalogStore.skillsAssessmentsAvailable,
    },
    {
      component: (
        <TrialSubscriptionsLane
          expanded={expandedLane === LaneIds.TrialSubscriptions}
          onTitleClick={() => handleTitleClick(LaneIds.TrialSubscriptions)}
        />
      ),
      id: LaneIds.TrialSubscriptions,
      visible: userStore.isRHLSTrialSubscriber,
    },
    {
      component: (
        <SkillsPathsLane
          expanded={expandedLane === LaneIds.SkillsPaths}
          onTitleClick={() => handleTitleClick(LaneIds.SkillsPaths)}
        />
      ),
      id: LaneIds.SkillsPaths,
      visible: showLearningPaths,
    },
    {
      component: (
        <NewOfferingsLane
          expanded={expandedLane === LaneIds.NewOfferings}
          onTitleClick={() => handleTitleClick(LaneIds.NewOfferings)}
        />
      ),
      id: LaneIds.NewOfferings,
      visible: newOfferingsEnabled,
    },
    {
      component: (
        <SavedForLaterLane
          expanded={expandedLane === LaneIds.SavedForLater}
          onTitleClick={() => handleTitleClick(LaneIds.SavedForLater)}
        />
      ),
      id: LaneIds.SavedForLater,
      visible: savedForLaterCardsEnabled,
    },
  ];

  if (!hasCurrentEnrollment) {
    return (
      <EmptyState>
        <EmptyStateHeader
          headingLevel="h4"
          icon={<EmptyStateIcon icon={ErrorIcon} />}
          titleText={t('No active enrollment')}
        />

        <EmptyStateBody>
          {t(
            'It looks like you do not have any active learning subscriptions or enrollments.',
          )}
        </EmptyStateBody>
      </EmptyState>
    );
  }

  return (
    <div className="my-training">
      <div className="my-training__content">
        <Banners />

        <section className="my-activity__section">
          <h2>
            {expandedLane ? (
              <Button
                className="my-training__back-link"
                icon={<ArrowLeftIcon aria-hidden />}
                iconPosition="start"
                onClick={() => handleTitleClick(null)}
                variant="link"
              >
                {t('My Training')}
              </Button>
            ) : (
              t('My Training')
            )}
          </h2>

          <div className="my-training__widgets">
            {isNewMyTrainingEnabled ? (
              <>
                {lanes
                  .filter(
                    ({ id, visible }) =>
                      visible && (!expandedLane || expandedLane === id),
                  )
                  .map(({ component, id }) => (
                    <div key={id} id={laneAnchorId(id)}>
                      {component}
                    </div>
                  ))}
              </>
            ) : (
              <>
                {/* Usage of this older widget is temporary */}
                <ClassesWidget
                  classes={classesStore.iltClasses}
                  modality="ilt"
                />

                <ClassesWidget classes={classesStore.vtClasses} modality="vt" />

                {userStore.isPremiumSubscriber && (
                  <ClassesWidget
                    classes={classesStore.premiumClassesWithEnrollments}
                    modality="premvt"
                    title={t('MY LIVE SESSIONS')}
                  />
                )}

                <MyProgressWidget allowShowMore collapsible />

                {userStore.isRHLSTrialSubscriber && <RHLSTrialWidget />}

                {showLearningPaths && <LearningPathWidget />}
              </>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default withErrorBoundary(
  observer(MyTraining),
  ComponentError,
  handleError,
);
